import { createContext, useState, useCallback, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Check, ChevronRight, Users, Calendar, DollarSign } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePersonalizationPreferences } from "@/hooks/usePersonalizationPreferences";
import { useAuth } from "@/hooks/useAuth";
import { isWelcomeFlowSuppressedRoute } from "@/components/layout/AuthenticatedProviders";
import { cn } from "@/lib/utils";

interface OnboardingStepMeta {
  id: "role" | "tour" | "favorites";
  title: string;
  description: string;
  icon: React.ElementType;
}

const ONBOARDING_STEPS: OnboardingStepMeta[] = [
  {
    id: "role",
    title: "Welcome to Forge!",
    description: "Let's personalize your experience",
    icon: Users,
  },
  {
    id: "tour",
    title: "Quick Tour",
    description: "Your top 3 tasks",
    icon: ChevronRight,
  },
  {
    id: "favorites",
    title: "Pin Your Favorites",
    description: "Quick access to the pages you use most",
    icon: Check,
  },
];

const ROLES: { label: string; desc: string }[] = [
  { label: "Church Administrator", desc: "Oversee operations and manage settings" },
  { label: "Pastor/Minister", desc: "Focus on people, events, and ministry" },
  { label: "Treasurer/Finance", desc: "Manage giving, accounting, and reports" },
  { label: "Secretary/Office", desc: "Handle day-to-day admin and communications" },
  { label: "Volunteer/Staff", desc: "Limited access for specific tasks" },
];

const TOUR_TASKS = [
  {
    icon: Users,
    title: "Manage People",
    desc: "Add members, update info, view households",
    label: "Go to People →",
    route: "/people",
  },
  {
    icon: Calendar,
    title: "Create Events",
    desc: "Schedule services, meetings, and activities",
    label: "Go to Calendar →",
    route: "/events",
  },
  {
    icon: DollarSign,
    title: "Record Giving",
    desc: "Enter offerings and track contributions",
    label: "Go to Giving →",
    route: "/giving",
  },
];

// Exported so other "is onboarding handled" checks (e.g. ChangelogModal's
// auto-popup gate) can recognize the same legacy client-only dismissal signal
// this file already reads at its own auto-popup gate below.
export const ONBOARDING_DISMISSED_KEY = "forge:onboarding:dismissed";

interface WelcomeFlowContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const WelcomeFlowContext = createContext<WelcomeFlowContextValue | null>(null);

/** Wraps the part of the tree that mounts <WelcomeFlow /> so its open state
 * is reachable from elsewhere (e.g. a "Replay tour" button in Settings) —
 * without this, WelcomeFlow's dialog visibility was local-only state with no
 * way to trigger it after the first-run auto-show had already dismissed. */
export function WelcomeFlowProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <WelcomeFlowContext.Provider value={{ open, setOpen }}>
      {children}
    </WelcomeFlowContext.Provider>
  );
}

function useWelcomeFlowContext() {
  const ctx = useContext(WelcomeFlowContext);
  if (!ctx) {
    throw new Error("WelcomeFlow components must be rendered within a WelcomeFlowProvider");
  }
  return ctx;
}

/** Reopens the welcome flow modal on demand, bypassing the dismissed/
 * completed gate that only governs the automatic first-run show. */
function useWelcomeFlowReplay() {
  const { setOpen } = useWelcomeFlowContext();
  return useCallback(() => setOpen(true), [setOpen]);
}

export function WelcomeFlow() {
  const { open, setOpen } = useWelcomeFlowContext();
  const [currentStep, setCurrentStep] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    preferences,
    setWelcomeCompleted,
    setPrimaryRole,
    isReady: preferencesReady,
  } = usePersonalizationPreferences();
  const { user } = useAuth();

  // Suppress the modal on public/auth/MFA routes while preserving MFA's
  // organization-scoped provider stack.
  const isWelcomeSuppressedRoute = isWelcomeFlowSuppressedRoute(location.pathname);

  // Close immediately if the user lands on a public/auth route while the modal is open.
  useEffect(() => {
    if (isWelcomeSuppressedRoute && open) setOpen(false);
  }, [isWelcomeSuppressedRoute, open, setOpen]);

  // Selected role tracks the role-step button the user clicked. Keep this
  // in sync with persisted preferences (including clearing to undefined) so
  // auth/account switches cannot leak a previous user's selected role.
  const [selectedRole, setSelectedRole] = useState<string | undefined>(undefined);

  useEffect(() => {
    setSelectedRole(preferences.primaryRole);
  }, [preferences.primaryRole]);

  // Every time the modal opens — including a manual replay via
  // useWelcomeFlowReplay(), which can happen well after a prior run left
  // currentStep on the last step — start from the beginning.
  useEffect(() => {
    if (open) setCurrentStep(0);
  }, [open]);

  // Check if onboarding should show — ONLY if:
  //  1. User is authenticated
  //  2. Preferences have finished hydrating (avoids race with server fetch)
  //  3. We're NOT on a public/auth/MFA route (avoids the modal popping between
  //     login and 2FA, or over a public page)
  useEffect(() => {
    if (!user) return;
    if (!preferencesReady) return;
    if (isWelcomeSuppressedRoute) return;

    try {
      const dismissed = localStorage.getItem(ONBOARDING_DISMISSED_KEY);
      if (!dismissed && !preferences.welcomeCompleted) {
        const timer = setTimeout(() => setOpen(true), 1000);
        return () => clearTimeout(timer);
      }
    } catch {
      // localStorage unavailable
    }
  }, [user, preferences.welcomeCompleted, preferencesReady, isWelcomeSuppressedRoute, setOpen]);

  // One dismissal handler used by every exit path: X button, Skip-for-now,
  // click-outside-the-dialog, Escape key, AND Get-Started. Persists to BOTH
  // localStorage (so the same browser doesn't re-trigger) AND server-side
  // preferences (so a different browser/device after first dismissal stays
  // dismissed). The previous version only persisted server-side from the
  // Get-Started path, which is why the modal kept popping up in private
  // browsing where localStorage doesn't carry between sessions.
  const persistDismiss = useCallback(() => {
    try {
      localStorage.setItem(ONBOARDING_DISMISSED_KEY, "true");
    } catch {
      // localStorage unavailable
    }
    setWelcomeCompleted(true);
  }, [setWelcomeCompleted]);

  const handleClose = useCallback(() => {
    persistDismiss();
    setOpen(false);
  }, [persistDismiss, setOpen]);

  const handleComplete = useCallback(() => {
    persistDismiss();
    setOpen(false);
  }, [persistDismiss, setOpen]);

  // Closes the modal (same as Skip/X) before navigating so the tour cards
  // don't leave a dismissed-but-still-open dialog stacked over the target page.
  // Some tour destinations are staff-only routes; a non-staff user landing on
  // one gets redirected by that route's own StaffAccessGate, same as clicking
  // any other staff-only link or bookmark in the app. Destination routes remain
  // the source of truth for access, rather than this onboarding modal.
  const handleTourTaskClick = useCallback(
    (route: string) => {
      persistDismiss();
      setOpen(false);
      navigate(route);
    },
    [persistDismiss, navigate],
  );

  // When the user clicks a role tile, persist the choice immediately so that
  // refreshing or navigating away mid-flow doesn't lose the selection.
  const handleSelectRole = useCallback(
    (label: string) => {
      setSelectedRole(label);
      setPrimaryRole(label);
    },
    [setPrimaryRole],
  );

  const handleNext = useCallback(() => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      handleComplete();
    }
  }, [currentStep, handleComplete]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  const step = ONBOARDING_STEPS[currentStep];
  const StepIcon = step.icon;
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;
  // Gate Next on the role step until a role is chosen — clicking Next without
  // a selection used to silently advance, contributing to the "modal serves
  // no purpose" feeling because the role step appeared interactive but
  // captured nothing.
  const canAdvance = step.id !== "role" || Boolean(selectedRole);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // Click-outside / Escape both call onOpenChange(false). Treat those
        // as a dismiss so the modal doesn't re-pop next session, matching
        // the behavior of the X and Skip buttons.
        if (!nextOpen) {
          persistDismiss();
        }
        setOpen(nextOpen);
      }}
    >
      <DialogContent className="sm:max-w-[580px]" hideCloseButton>
        <DialogHeader>
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <StepIcon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl">{step.title}</DialogTitle>
                <DialogDescription>{step.description}</DialogDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleClose}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="py-4">
          {step.id === "role" && (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                What's your primary role at the church? We'll highlight the features you use most.
              </p>
              <div className="grid gap-3">
                {ROLES.map((role) => {
                  const isSelected = selectedRole === role.label;
                  return (
                    <button
                      key={role.label}
                      type="button"
                      onClick={() => handleSelectRole(role.label)}
                      aria-pressed={isSelected}
                      className={cn(
                        "text-left rounded-lg border-2 p-4 transition-colors",
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50",
                      )}
                    >
                      <div className="font-medium flex items-center justify-between gap-2">
                        <span>{role.label}</span>
                        {isSelected && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
                      </div>
                      <div className="text-sm text-muted-foreground">{role.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step.id === "tour" && (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Here are the three things you'll do most often in Forge:
              </p>
              <div className="space-y-3">
                {TOUR_TASKS.map((task) => {
                  const Icon = task.icon;
                  return (
                    <button
                      key={task.title}
                      type="button"
                      onClick={() => handleTourTaskClick(task.route)}
                      className="flex w-full items-start gap-4 rounded-lg border p-4 text-left transition-colors hover:border-primary/50"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{task.title}</div>
                        <div className="text-sm text-muted-foreground">{task.desc}</div>
                        <div className="text-sm text-primary mt-1">{task.label}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step.id === "favorites" && (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                You can pin any page for quick access from the sidebar. Just hover over a menu item and click the pin icon.
              </p>
              <div className="rounded-lg border bg-muted/50 p-6 text-center">
                <div className="mx-auto w-fit rounded-lg bg-background border p-3 mb-3">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <p className="font-medium">Try it yourself!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Hover over "People" in the sidebar and click the pin
                </p>
              </div>
              <div className="bg-amber-50 dark:bg-warning-700/[0.12] border border-amber-200 dark:border-warning-700/30 rounded-lg p-4">
                <p className="text-sm">
                  <strong>Pro tip:</strong> Use <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-background border rounded">Cmd+K</kbd> to quickly search and jump to any page.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 py-3">
          {ONBOARDING_STEPS.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={cn(
                "h-2 rounded-full transition-all",
                index === currentStep
                  ? "w-8 bg-primary"
                  : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
              )}
              aria-label={`Go to step ${index + 1}`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-3 pt-4 border-t">
          <Button
            variant="ghost"
            onClick={handleClose}
            className="text-muted-foreground"
          >
            Skip for now
          </Button>
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
            )}
            <Button onClick={handleNext} disabled={!canAdvance}>
              {isLastStep ? "Get Started" : "Next"}
              {!isLastStep && <ChevronRight className="ml-1 h-4 w-4" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Reopens the onboarding tour. Must be rendered within the same
 * WelcomeFlowProvider that wraps the mounted <WelcomeFlow />. */
export function ReopenWelcomeButton() {
  const replay = useWelcomeFlowReplay();

  return (
    <Button variant="outline" size="sm" onClick={replay}>
      View Welcome Tour
    </Button>
  );
}
